import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  FormControlLabel,
  Radio,
  RadioGroup,
  CircularProgress,
  Typography
} from '@mui/material';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { useProgressContext } from '../components/ModulePageLayout';
import { useDiagnosticAccessGuard } from '../hooks/useDiagnosticAccessGuard';
import { useModuleTimeTracker } from '../hooks/useModuleTimeTracker';
import { fetchStudentCourse, progressAPI, quizAPI } from '../services/api';
import { getLearnerFacingApiError } from '../utils/learnerErrors';
import '../styles/components/_quiz.scss';
import {
  CheckIcon,
  CloseIcon,
  InfoIcon,
} from '../utils/icons';
import { getLocalizedText, getDisplayLocale } from '../utils/i18n';
import { getNextItem, getNextItemPath } from '../utils/navigation';
import { normalizeId } from '../utils/id';

interface QuizOption {
  id?: string;
  [key: string]: string | undefined;
}

interface QuizQuestion {
  _id?: string;
  question: string | { [key: string]: string };
  options: Array<string | QuizOption>;
  correctAnswerIndex?: number;
  order: number;
}

interface Quiz {
  _id: string;
  title: string | { [key: string]: string };
  description?: string | { [key: string]: string };
  questions: QuizQuestion[];
  passingPercentage?: number;
  isDiagnostic: boolean;
}

interface QuizResponse {
  answers: Array<{
    questionId?: string;
    questionOrder: number;
    selectedOptionId?: string;
    selectedAnswerIndex: number;
    isCorrect?: boolean;
    feedback?: string;
  }>;
  score?: number;
  passed?: boolean;
  completedAt?: string;
}

const getQuestionId = (question: QuizQuestion | null | undefined): string | undefined =>
  question?._id ? String(question._id) : undefined;

const getOptionId = (question: QuizQuestion, optionIndex: number): string | undefined => {
  const option = question.options?.[optionIndex];
  if (option && typeof option === 'object' && option.id) {
    return String(option.id);
  }
  return undefined;
};

const answerMatchesQuestion = (answer: QuizResponse['answers'][number], question: QuizQuestion): boolean => {
  const questionId = getQuestionId(question);
  if (questionId && answer.questionId) {
    return answer.questionId === questionId;
  }
  return Number(answer.questionOrder) === Number(question.order);
};

const resolveQuestionOrderFromAnswer = (
  answer: QuizResponse['answers'][number],
  questions: QuizQuestion[]
): number | undefined => {
  if (answer.questionId) {
    const match = questions.find((q) => getQuestionId(q) === answer.questionId);
    if (match) return match.order;
  }
  return answer.questionOrder;
};

const getEffectivePassed = (response: QuizResponse | null | undefined, quiz: Quiz | null | undefined): boolean => {
  if (!response || !quiz || quiz.isDiagnostic) return false;
  if (response.passed === true) return true;
  if (response.passed === false) return false;
  const score = response.score;
  if (typeof score !== 'number') return false;
  const passingPercentage = quiz.passingPercentage ?? 70;
  return score >= passingPercentage;
};

type QuizState =
  | 'loading'
  | 'diagnostic-already-done'
  | 'quiz-completed-success'
  | 'quiz-completed-failed'
  | 'quiz-in-progress';

const Quiz: React.FC = () => {
  const { courseId, chapterId, quizId: quizIdParam, quizOrder } = useParams<{
    courseId: string;
    chapterId: string;
    quizId?: string;
    quizOrder?: string;
  }>();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const { refreshProgress, refreshAllProgress, refreshCourse } = useProgressContext();
  useDiagnosticAccessGuard();

  const [course, setCourse] = useState<any>(null);

  const [quiz, setQuiz] = useState<Quiz | null>(null);
  const [quizId, setQuizId] = useState<string | null>(null); // Store quiz ID
  const [quizResponse, setQuizResponse] = useState<QuizResponse | null>(null);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [selectedAnswer, setSelectedAnswer] = useState<number | null>(null);
  const [answeredQuestions, setAnsweredQuestions] = useState<Set<number>>(new Set());
  const [questionFeedback, setQuestionFeedback] = useState<{ [key: number]: { isCorrect: boolean; feedback?: string } }>({});
  const [state, setState] = useState<QuizState>('loading');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [retrying, setRetrying] = useState(false);

  const loadingRef = useRef(false);

  // Track time spent on this quiz
  useModuleTimeTracker({
    courseId: courseId || '',
    chapterId: chapterId || '',
    moduleType: 'quiz',
    moduleId: quizId || '',
    enabled: !!courseId && !!chapterId && !!quizId && state === 'quiz-in-progress'
  });

  useEffect(() => {
    if (courseId && chapterId && (quizIdParam || quizOrder) && !loadingRef.current) {
      loadingRef.current = true;
      loadQuizData().finally(() => {
        loadingRef.current = false;
      });
    }
  }, [courseId, chapterId, quizIdParam, quizOrder]);


  const loadQuizData = async () => {
    if (!courseId || !chapterId || (!quizIdParam && !quizOrder)) {
      setError(t('common.missingParameters'));
      setState('loading');
      return;
    }

    try {
      setState('loading');
      setError(null);

      // Load course data
      const courseData = await fetchStudentCourse(courseId, { full: true });
      setCourse(courseData);

      // Find the quiz in the chapter
      const chapter = courseData.chapters?.find((ch: any) => normalizeId(ch._id) === normalizeId(chapterId));
      if (!chapter) {
        setError(t('quiz.chapterNotFound'));
        return;
      }

      let quizItem: any = null;
      if (quizIdParam) {
        quizItem = chapter.items?.find((item: any) => item.itemType === 'quiz' && normalizeId(item?._id || item?.id) === normalizeId(quizIdParam));
        // Legacy compatibility: old URLs used order in place of id
        if (!quizItem && /^\d+$/.test(quizIdParam)) {
          const legacyOrder = parseInt(quizIdParam, 10);
          quizItem = chapter.items?.find((item: any) => item.itemType === 'quiz' && item.order === legacyOrder);
        }
      } else if (quizOrder) {
        const quizOrderNum = parseInt(quizOrder);
        quizItem = chapter.items?.find((item: any) => item.itemType === 'quiz' && item.order === quizOrderNum);
      }

      if (!quizItem) {
        setError(t('quiz.notFound'));
        return;
      }

      console.log('📋 Quiz item found:', {
        _id: quizItem._id,
        order: quizItem.order,
        isDiagnostic: quizItem.isDiagnostic,
        isCompleted: quizItem.isCompleted
      });

      // Log only if questions are missing
      if (!quizItem.questions || quizItem.questions.length === 0) {
        console.error('❌ Quiz item has no questions:', {
          _id: quizItem._id,
          order: quizItem.order,
          itemType: quizItem.itemType
        });
      } else {
        console.log('✅ Quiz loaded:', {
          _id: quizItem._id,
          questionsCount: quizItem.questions.length
        });
      }

      setQuiz(quizItem);
      setQuizId(normalizeId(quizItem._id) || null);

      if (!quizIdParam && quizOrder && normalizeId(quizItem?._id)) {
        navigate(
          `/course/${courseId}/chapter/${chapterId}/quiz/${normalizeId(quizItem._id)}`,
          { replace: true }
        );
      }

      // Try to load existing response using quizId
      try {
        const quizItemId = normalizeId(quizItem._id);
        if (!quizItemId) {
          // Skip loading response if quiz ID is missing
          console.warn('Quiz ID not found, skipping response load');
        } else {
          console.log('🔍 Loading quiz response for diagnostic check:', {
            chapterId,
            quizId: quizItemId,
            isDiagnostic: quizItem.isDiagnostic
          });

          const response = await quizAPI.getQuizResponse(chapterId, quizItemId);

          console.log('📦 Quiz response received:', {
            hasResponse: !!response,
            hasAnswers: !!(response?.answers),
            answersLength: response?.answers?.length || 0,
            hasCompletedAt: !!response?.completedAt,
            completedAt: response?.completedAt,
            isDiagnostic: quizItem.isDiagnostic
          });

          if (response) {
            setQuizResponse(response);

            // Check if it's a diagnostic quiz
            if (quizItem.isDiagnostic) {
              // For diagnostic quizzes, if there's any response (answers or completedAt), it's already done
              // Also check if the item is marked as completed in the course data
              const hasAnswers = response.answers && response.answers.length > 0;
              const hasCompletedAt = !!response.completedAt;
              const isItemCompleted = quizItem.isCompleted === true;

              console.log('🔎 Diagnostic check:', {
                isDiagnostic: true,
                hasAnswers,
                hasCompletedAt,
                isItemCompleted,
                shouldShowMessage: hasAnswers || hasCompletedAt || isItemCompleted
              });

              if (hasAnswers || hasCompletedAt || isItemCompleted) {
                // Diagnostic already done, show message
                console.log('✅ Diagnostic already done, showing message');
                setState('diagnostic-already-done');
                return;
              } else {
                console.log('⚠️ Diagnostic quiz but no response found, allowing to start');
              }
            }

            // For normal quizzes, check if there are answers
            if (response.answers && response.answers.length > 0) {
              // For normal quiz, check if completed and passed/failed
              if (response.completedAt) {
                if (getEffectivePassed(response, quizItem)) {
                  setState('quiz-completed-success');
                } else {
                  setState('quiz-completed-failed');
                }
                return;
              }

              // Quiz in progress, restore state
              const answeredSet = new Set<number>(
                response.answers.map((a: QuizResponse['answers'][number]) =>
                  resolveQuestionOrderFromAnswer(a, quizItem.questions) ?? a.questionOrder
                )
              );
              setAnsweredQuestions(answeredSet);

              const feedbackMap: { [key: number]: { isCorrect: boolean; feedback?: string } } = {};
              response.answers.forEach((answer: QuizResponse['answers'][number]) => {
                const questionOrder =
                  resolveQuestionOrderFromAnswer(answer, quizItem.questions) ?? answer.questionOrder;
                if (answer.isCorrect !== undefined) {
                  feedbackMap[questionOrder] = {
                    isCorrect: answer.isCorrect,
                    feedback: answer.feedback
                  };
                }
              });
              setQuestionFeedback(feedbackMap);

              // Find first unanswered question
              // Sort questions by order first to ensure we're checking in the right sequence
              const sortedQuestions = [...quizItem.questions].sort((a, b) => a.order - b.order);
              const firstUnanswered = sortedQuestions.findIndex(
                (q: QuizQuestion) => !answeredSet.has(q.order)
              );
              if (firstUnanswered >= 0) {
                // Find the index in the original questions array
                const questionOrder = sortedQuestions[firstUnanswered].order;
                const originalIndex = quizItem.questions.findIndex((q: QuizQuestion) => q.order === questionOrder);
                console.log('📝 Restoring quiz state:', {
                  firstUnansweredIndex: firstUnanswered,
                  questionOrder,
                  originalIndex,
                  totalQuestions: quizItem.questions.length
                });
                if (originalIndex >= 0) {
                  setCurrentQuestionIndex(originalIndex);
                  // Restore selectedAnswer if this question was already answered
                  const existingAnswer = response.answers.find((a: QuizResponse['answers'][number]) =>
                    answerMatchesQuestion(a, sortedQuestions[firstUnanswered])
                  );
                  if (existingAnswer) {
                    setSelectedAnswer(existingAnswer.selectedAnswerIndex);
                  }
                } else {
                  setCurrentQuestionIndex(0);
                }
              } else {
                // All questions answered but not completed (shouldn't happen normally)
                console.log('⚠️ All questions answered but quiz not completed');
                setState('quiz-completed-failed');
              }
            }
          }
        }
      } catch (error: any) {
        // No existing response, start fresh
        if (error.response?.status === 404) {
          console.log('ℹ️ No quiz response found (404), checking if quiz is completed in course data');
          // For diagnostic quizzes, also check if the item is marked as completed in the course data
          if (quizItem.isDiagnostic && quizItem.isCompleted === true) {
            console.log('✅ Diagnostic quiz marked as completed in course data, showing message');
            setState('diagnostic-already-done');
            return;
          } else {
            console.log('ℹ️ Diagnostic quiz with no response and not marked as completed, allowing to start');
          }
        } else {
          console.error('❌ Error loading quiz response:', error);
        }
      }

      // If we get here, quiz is ready to start or continue
      setState('quiz-in-progress');
    } catch (err: unknown) {
      console.error('Error loading quiz:', err);
      setError(getLearnerFacingApiError(err, t));
    }
  };

  const handleAnswerSelect = (answerIndex: number) => {
    if (!answeredQuestions.has(currentQuestion?.order || -1)) {
      setSelectedAnswer(answerIndex);
    }
  };

  const handleSubmitAnswer = async () => {
    if (!quiz || !chapterId || !quizId || selectedAnswer === null) {
      return;
    }

    const currentQuestion = quiz.questions[currentQuestionIndex];
    if (!currentQuestion) {
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      const answer = {
        questionId: getQuestionId(currentQuestion),
        questionOrder: currentQuestion.order,
        selectedOptionId: getOptionId(currentQuestion, selectedAnswer),
        selectedAnswerIndex: selectedAnswer
      };

      const response = await quizAPI.submitQuiz(
        chapterId,
        quizId,
        [answer]
      );

      // The API returns { message: '...', response: { ...quizResponse, answers: [...] } }
      // So we need to extract the response object
      const quizResponseData = response.response || response;
      setQuizResponse(quizResponseData);

      console.log('📝 Quiz response received:', {
        fullResponse: response,
        responseResponse: response.response,
        quizResponseData,
        answers: quizResponseData?.answers,
        answersType: typeof quizResponseData?.answers,
        answersIsArray: Array.isArray(quizResponseData?.answers),
        answersLength: quizResponseData?.answers?.length,
        currentQuestionOrder: currentQuestion.order,
        currentQuestionOrderType: typeof currentQuestion.order
      });

      // Update answered questions
      setAnsweredQuestions(prev => {
        const newSet = new Set<number>([...Array.from(prev), currentQuestion.order]);
        console.log('✅ Updated answeredQuestions:', Array.from(newSet));
        return newSet;
      });

      // For diagnostic quizzes, no feedback
      if (!quiz.isDiagnostic && quizResponseData.answers && quizResponseData.answers.length > 0) {
        console.log('🔍 Looking for feedback in answers:', {
          answers: quizResponseData.answers,
          currentQuestionOrder: currentQuestion.order,
          answersWithOrder: quizResponseData.answers.map((a: any) => ({
            questionOrder: a.questionOrder,
            type: typeof a.questionOrder,
            hasIsCorrect: 'isCorrect' in a,
            isCorrect: a.isCorrect,
            hasFeedback: 'feedback' in a,
            feedback: a.feedback
          }))
        });

        const result = quizResponseData.answers.find((ans: QuizResponse['answers'][number]) =>
          answerMatchesQuestion(ans, currentQuestion)
        );

        console.log('🎯 Feedback result found:', result);

        if (result) {
          const feedbackData = {
            isCorrect: result.isCorrect !== undefined ? result.isCorrect : false,
            feedback: result.feedback || ''
          };

          console.log('💾 Storing feedback:', {
            questionOrder: currentQuestion.order,
            feedbackData
          });

          setQuestionFeedback(prev => {
            const newFeedback = {
              ...prev,
              [currentQuestion.order]: feedbackData
            };
            console.log('✅ Updated questionFeedback:', newFeedback);
            return newFeedback;
          });
        } else {
          console.warn('⚠️ No feedback result found for question order:', {
            currentQuestionOrder: currentQuestion.order,
            availableOrders: quizResponseData.answers.map((a: any) => a.questionOrder)
          });
        }
      } else {
        console.log('ℹ️ No feedback (diagnostic or no answers):', {
          isDiagnostic: quiz.isDiagnostic,
          hasAnswers: !!quizResponseData.answers,
          answersLength: quizResponseData.answers?.length
        });
      }

      // Don't reset selectedAnswer - keep it to show the color feedback
      // It will be reset when moving to the next question
      console.log('🎨 Keeping selectedAnswer:', selectedAnswer);

      // For diagnostic quizzes, automatically move to next question (no feedback to show)
      if (quiz.isDiagnostic) {
        // Small delay to ensure state is updated
        setTimeout(() => {
          handleNextQuestion();
        }, 100);
      }
    } catch (error: unknown) {
      console.error('Error submitting answer:', error);
      setError(getLearnerFacingApiError(error, t));
    } finally {
      setSubmitting(false);
    }
  };

  const handleNextQuestion = () => {
    if (!quiz) return;

    console.log('➡️ handleNextQuestion called:', {
      currentQuestionIndex,
      totalQuestions: quiz.questions.length,
      isLastQuestion: currentQuestionIndex === quiz.questions.length - 1
    });

    if (currentQuestionIndex < quiz.questions.length - 1) {
      // Move to next question
      const nextIndex = currentQuestionIndex + 1;
      const nextQuestion = quiz.questions[nextIndex];

      // Check if next question was already answered
      const nextQuestionAnswered = answeredQuestions.has(nextQuestion?.order || -1);
      const nextQuestionFeedback = questionFeedback[nextQuestion?.order || -1];

      // If next question was answered, restore the selected answer
      if (nextQuestionAnswered && quizResponse) {
        const existingAnswer = quizResponse.answers.find((a) =>
          answerMatchesQuestion(a, nextQuestion)
        );
        if (existingAnswer) {
          setSelectedAnswer(existingAnswer.selectedAnswerIndex);
        } else {
          setSelectedAnswer(null);
        }
      } else {
        setSelectedAnswer(null);
      }

      setCurrentQuestionIndex(nextIndex);
    } else {
      // All questions answered, complete the quiz
      console.log('✅ All questions answered, completing quiz');
      completeQuiz();
    }
  };

  const completeQuiz = async () => {
    if (!quiz || !courseId || !chapterId || !quizId || !course) {
      return;
    }

    try {
      setSubmitting(true);

      // Find quiz item by ID to get its order for navigation
      const chapter = course.chapters?.find((ch: any) => normalizeId(ch._id) === normalizeId(chapterId));
      const quizItem = chapter?.items?.find(
        (item: any) =>
          item.itemType === 'quiz' && normalizeId(item._id || item.id) === normalizeId(quizId)
      );
      const quizOrderNum = quizItem?.order || 0;

      // For diagnostic, mark as complete
      if (quiz.isDiagnostic) {
        if (quizItem?._id) {
          await progressAPI.markItemComplete(courseId, chapterId, 'quiz', normalizeId(quizItem._id), quizOrderNum);
          await refreshCourse(); // Reload course data to update item.isCompleted
          await refreshProgress();
          await refreshAllProgress();
        }

        // Navigate to next item
        const nextItem = getNextItem(course, chapterId, quizId || '');
        const nextPath = getNextItemPath(nextItem);
        if (nextPath) {
          navigate(nextPath);
        } else {
          navigate(`/course/${courseId}`);
        }
        return;
      }

      // For normal quiz, check final score (merge GET + last submit payload; tolerate stale GET)
      const remote = await quizAPI.getQuizResponse(chapterId, quizId);
      let merged: QuizResponse = {
        ...(quizResponse || {}),
        ...remote,
        answers:
          Array.isArray(remote?.answers) && remote.answers.length > 0 ? remote.answers : quizResponse?.answers,
        score: typeof remote?.score === 'number' ? remote.score : quizResponse?.score,
        passed: typeof remote?.passed === 'boolean' ? remote.passed : quizResponse?.passed,
        completedAt: remote?.completedAt ?? quizResponse?.completedAt
      };

      if (
        !quiz.isDiagnostic &&
        typeof merged.score !== 'number' &&
        Array.isArray(merged.answers) &&
        merged.answers.length >= quiz.questions.length
      ) {
        let correctCount = 0;
        for (const ans of merged.answers) {
          const q = quiz.questions.find((qx) => answerMatchesQuestion(ans, qx));
          if (q && q.correctAnswerIndex === ans.selectedAnswerIndex) correctCount += 1;
        }
        const passingPercentage = quiz.passingPercentage ?? 70;
        const computed = Math.round((correctCount / quiz.questions.length) * 100);
        merged = { ...merged, score: computed, passed: computed >= passingPercentage };
      }

      const answersCount = Array.isArray(merged?.answers) ? merged.answers.length : 0;
      const expectedOrders = new Set(quiz.questions.map((q) => Number(q.order)));
      const answeredOrders = new Set(Array.from(answeredQuestions).map(Number));
      const allOrdersSubmitted = Array.from(expectedOrders).every((o) => answeredOrders.has(o));

      const hasFinalResult =
        Boolean(merged?.completedAt) ||
        typeof merged?.score === 'number' ||
        answersCount >= quiz.questions.length ||
        allOrdersSubmitted;

      if (hasFinalResult) {
        // Update local state with the freshest response payload
        setQuizResponse(merged);

        if (getEffectivePassed(merged, quiz)) {
          setState('quiz-completed-success');

          // Mark as complete
          const itemId = normalizeId(quizItem?._id || quizItem?.id || quizId);
          if (itemId) {
            await progressAPI.markItemComplete(courseId, chapterId, 'quiz', itemId, quizOrderNum);
            await refreshCourse(); // Reload course data to update item.isCompleted
            await refreshProgress();
            await refreshAllProgress();
          }
        } else {
          setState('quiz-completed-failed');
        }
      } else {
        // Safety net: keep user informed instead of silently staying on same question.
        setError(t('quiz.completeError'));
      }
    } catch (error: unknown) {
      console.error('Error completing quiz:', error);
      setError(getLearnerFacingApiError(error, t));
    } finally {
      setSubmitting(false);
    }
  };

  const handleRetry = async () => {
    if (!chapterId || !quizId) {
      return;
    }

    try {
      setRetrying(true);
      setError(null);

      // Delete existing response to allow retry
      await quizAPI.deleteQuizResponse(chapterId, quizId);

      // Reset state
      setState('quiz-in-progress');
      setCurrentQuestionIndex(0);
      setSelectedAnswer(null);
      setAnsweredQuestions(new Set<number>());
      setQuestionFeedback({});
      setQuizResponse(null);
    } catch (error: unknown) {
      console.error('Error retrying quiz:', error);
      setError(getLearnerFacingApiError(error, t));
    } finally {
      setRetrying(false);
    }
  };

  const handleContinue = () => {
    if (!courseId || !chapterId || !quizId || !course) {
      return;
    }

    // Find quiz item to get its order for navigation
    const chapter = course.chapters?.find((ch: any) => normalizeId(ch._id) === normalizeId(chapterId));
    const quizItem = chapter?.items?.find((item: any) => item.itemType === 'quiz' && item._id?.toString() === quizId);
    const quizOrderNum = quizItem?.order || 0;

    const nextItem = getNextItem(course, chapterId, quizId || '');
    const nextPath = getNextItemPath(nextItem);
    if (nextPath) {
      navigate(nextPath);
    } else {
      navigate(`/course/${courseId}`);
    }
  };

  // Loading state
  if (state === 'loading') {
    return (
      <Box className="quiz__loading">
        <CircularProgress color="inherit" />
      </Box>
    );
  }

  // Error state
  if (error && !quiz) {
    return (
      <Box className="quiz__error">
        <Typography variant="h6" className="quiz__error-text">
          {error}
        </Typography>
      </Box>
    );
  }

  if (!quiz) {
    return null;
  }

  const currentQuestion = quiz.questions[currentQuestionIndex];
  const isAnswered = answeredQuestions.has(currentQuestion?.order || -1);
  const feedback = questionFeedback[currentQuestion?.order || -1];
  const isLastQuestion = currentQuestionIndex === quiz.questions.length - 1;

  // Diagnostic already done page
  if (state === 'diagnostic-already-done') {
    const lang = i18n.language.split('-')[0] || 'fr';
    const locale = getDisplayLocale(lang);
    const completedDate = quizResponse?.completedAt
      ? new Date(quizResponse.completedAt).toLocaleDateString(locale, {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      })
      : null;

    return (
      <Box className="quiz">
        <Card className="quiz__card">
          <CardContent className="quiz__card-content">
            {/* Alert Banner */}
            <Alert
              severity="info"
              icon={<InfoIcon size={24} />}
              className="quiz__alert"
            >
              <Typography variant="h6" className="quiz__alert-title">
                {t('quiz.diagnosticAlreadyDone')}
              </Typography>
              <Typography variant="body2" className="quiz__alert-text">
                {t('quiz.diagnosticAlreadyDoneMessage')}
              </Typography>
            </Alert>

            {/* Details Section */}
            <Box className="quiz__details-section">
              <Typography variant="subtitle1" className="quiz__details-title">
                {t('quiz.diagnosticDetails')}
              </Typography>
              <Box className="quiz__details-list">
                <Box className="quiz__details-item">
                  <Typography variant="body2" className="quiz__details-label">
                    {t('quiz.quizLabel')}
                  </Typography>
                  <Typography variant="body2" className="quiz__details-value">
                    {quiz ? getLocalizedText(quiz.title) : 'N/A'}
                  </Typography>
                </Box>
                {completedDate && (
                  <Box className="quiz__details-item">
                    <Typography variant="body2" className="quiz__details-label">
                      {t('quiz.completedOn')}
                    </Typography>
                    <Typography variant="body2" className="quiz__details-value">
                      {completedDate}
                    </Typography>
                  </Box>
                )}
                {quizResponse?.score !== undefined && (
                  <Box className="quiz__details-item">
                    <Typography variant="body2" className="quiz__details-label">
                      {t('quiz.score')}
                    </Typography>
                    <Typography variant="body2" className="quiz__details-value quiz__details-value--score">
                      {Math.round(quizResponse.score)}%
                    </Typography>
                  </Box>
                )}
                <Box className="quiz__details-item">
                  <Typography variant="body2" className="quiz__details-label">
                    {t('quiz.status')}
                  </Typography>
                  <Typography variant="body2" className="quiz__details-value quiz__details-value--status">
                    {t('quiz.statusCompleted')}
                  </Typography>
                </Box>
              </Box>
            </Box>

            {/* Action Button */}
            <Box className="quiz__actions quiz__actions--centered">
              <Button
                variant="contained"
                onClick={handleContinue}
                className="button-primary quiz__button"
              >
                {t('quiz.continueButton')}
              </Button>
            </Box>
          </CardContent>
        </Card>
      </Box>
    );
  }

  // Quiz completed successfully page
  if (state === 'quiz-completed-success') {
    const score = Math.round(quizResponse?.score || 0);
    const passingPercentage = quiz.passingPercentage || 70;
    const thresholdAngle = Math.min(100, Math.max(0, passingPercentage)) * 3.6;
    return (
      <Box className="quiz">
        <Card className="quiz__result-card quiz__result-card--success">
          <CardContent className="quiz__result-content">
            <p className="quiz__result-label">{t('quiz.yourScore')}</p>

            <Box className="quiz__result-gauge" aria-hidden={false}>
              <CircularProgress
                variant="determinate"
                value={100}
                size={240}
                thickness={(32 * 44) / 240}
                className="quiz__result-gauge-track"
              />
              <CircularProgress
                variant="determinate"
                value={Math.min(100, Math.max(0, score))}
                size={240}
                thickness={(32 * 44) / 240}
                className="quiz__result-gauge-fill"
              />
              <span
                className="quiz__result-gauge-threshold"
                style={{ transform: `rotate(${thresholdAngle}deg)` }}
                aria-hidden
              />
              <Box className="quiz__result-gauge-center">
                <CheckIcon size={48} strokeWidth={3} className="quiz__result-gauge-icon" />
                <p className="quiz__result-gauge-score">{score}%</p>
              </Box>
            </Box>

            <p className="quiz__result-title">{t('quiz.congratulations')}</p>
            <p className="quiz__result-text">{t('quiz.successMessage', { score })}</p>

            <Box className="quiz__actions quiz__actions--centered">
              <Button
                variant="contained"
                onClick={handleContinue}
                className="button-primary quiz__button"
              >
                {t('quiz.continueButton')}
              </Button>
            </Box>
          </CardContent>
        </Card>
      </Box>
    );
  }

  // Quiz completed failed page
  if (state === 'quiz-completed-failed') {
    const score = Math.round(quizResponse?.score || 0);
    const passingPercentage = quiz.passingPercentage || 70;
    const thresholdAngle = Math.min(100, Math.max(0, passingPercentage)) * 3.6;
    return (
      <Box className="quiz">
        <Card className="quiz__result-card quiz__result-card--failed">
          <CardContent className="quiz__result-content">
            <p className="quiz__result-label">{t('quiz.yourScore')}</p>

            <Box className="quiz__result-gauge" aria-hidden={false}>
              <CircularProgress
                variant="determinate"
                value={100}
                size={240}
                thickness={(32 * 44) / 240}
                className="quiz__result-gauge-track"
              />
              <CircularProgress
                variant="determinate"
                value={Math.min(100, Math.max(0, score))}
                size={240}
                thickness={(32 * 44) / 240}
                className="quiz__result-gauge-fill"
              />
              <span
                className="quiz__result-gauge-threshold"
                style={{ transform: `rotate(${thresholdAngle}deg)` }}
                aria-hidden
              />
              <Box className="quiz__result-gauge-center">
                <CloseIcon size={48} strokeWidth={3} className="quiz__result-gauge-icon" />
                <p className="quiz__result-gauge-score">{score}%</p>
              </Box>
            </Box>

            <p className="quiz__result-title">{t('quiz.insufficientScore')}</p>
            <p className="quiz__result-text">
              {t('quiz.failedCertificateMessage', { required: passingPercentage })}
            </p>

            <Box className="quiz__actions quiz__actions--centered">
              <Button
                variant="contained"
                onClick={handleRetry}
                disabled={retrying}
                className="button-primary quiz__button"
              >
                {retrying ? t('quiz.retrying') : t('quiz.retryButton')}
              </Button>
            </Box>
          </CardContent>
        </Card>
      </Box>
    );
  }

  // Quiz in progress - show current question
  return (
    <Box className="quiz">
      <Card className="quiz__question-card">
        <CardContent className="quiz__card-content">
          <div className="quiz__card-header">
            {/* Question Header */}
            <p
              className="quiz__question-header"
            >
              {t('quiz.questionLabel')} {currentQuestionIndex + 1} {t('quiz.of')} {quiz.questions.length}
            </p>

            {/* Question Text */}
            <p
              className="quiz__question-text"
            >
              {getLocalizedText(currentQuestion.question)}
            </p>

            {/* Instructions */}
            <p
              className="quiz__question-instructions"
            >
              {quiz.isDiagnostic
                ? t('quiz.selectAnswer')
                : t('quiz.checkCorrectAnswer')}
            </p>
          </div>
          {/* Error Message */}
          {error && (
            <Alert severity="error" className="quiz__error-alert">
              {error}
            </Alert>
          )}

          <Box className="quiz__question-body">
            {/* Options */}
            <RadioGroup
              value={selectedAnswer?.toString() || ''}
              onChange={(e) => handleAnswerSelect(parseInt(e.target.value))}
              className={`quiz__options ${isAnswered ? 'quiz__options--answered' : ''}`}
              sx={{
                pointerEvents: isAnswered ? 'none' : 'auto',
              }}
            >
              {currentQuestion.options.map((option, index) => {
                const isSelected = selectedAnswer === index;
                const showResult = isAnswered && feedback && !quiz.isDiagnostic;
                const isCorrectOption = index === currentQuestion.correctAnswerIndex;
                const isCorrectResult = Boolean(showResult && isSelected && feedback?.isCorrect);
                const isIncorrectResult = Boolean(showResult && isSelected && !feedback?.isCorrect);
                // Wrong answer: outline the real correct option in success color (border only).
                const revealCorrectBorder = Boolean(
                  showResult && !feedback?.isCorrect && isCorrectOption && !isSelected
                );

                const optionClasses = [
                  'quiz__option',
                  isAnswered ? 'quiz__option--answered' : '',
                  isSelected && !showResult ? 'quiz__option--selected' : '',
                  isCorrectResult ? 'quiz__option--correct' : '',
                  isIncorrectResult ? 'quiz__option--incorrect' : '',
                  revealCorrectBorder ? 'quiz__option--reveal-correct' : '',
                ]
                  .filter(Boolean)
                  .join(' ');

                const labelClasses = [
                  'quiz__option-label',
                  isCorrectResult ? 'quiz__option-label--correct' : '',
                  isIncorrectResult ? 'quiz__option-label--incorrect' : '',
                ]
                  .filter(Boolean)
                  .join(' ');

                let resultIcon: React.ReactNode = null;
                if (showResult) {
                  if (isCorrectOption) {
                    resultIcon = (
                      <CheckIcon
                        size={24}
                        strokeWidth={3}
                        className="quiz__option-status-icon quiz__option-status-icon--success"
                      />
                    );
                  } else if (isSelected) {
                    resultIcon = (
                      <CloseIcon
                        size={24}
                        strokeWidth={3}
                        className="quiz__option-status-icon quiz__option-status-icon--error"
                      />
                    );
                  } else {
                    resultIcon = (
                      <CloseIcon
                        size={24}
                        strokeWidth={3}
                        className="quiz__option-status-icon quiz__option-status-icon--muted"
                      />
                    );
                  }
                }

                return (
                  <FormControlLabel
                    key={index}
                    value={index.toString()}
                    className={optionClasses}
                    control={
                      showResult ? (
                        <Box className="quiz__option-status" component="span">
                          {resultIcon}
                        </Box>
                      ) : (
                        <Radio className="quiz__option-radio" disabled={isAnswered} />
                      )
                    }
                    label={<Typography className={labelClasses}>{getLocalizedText(option)}</Typography>}
                  />
                );
              })}
            </RadioGroup>
          </Box>

          {/* Feedback (only for non-diagnostic quizzes) — hidden for now with the new option icons UI; keep for later.
          {isAnswered && feedback && quiz && !quiz.isDiagnostic && (
            <Alert
              icon={feedback.isCorrect ? <ThumbUpIcon /> : <WarningIcon />}
              severity={feedback.isCorrect ? 'success' : 'error'}
              className={`quiz__feedback ${feedback.isCorrect ? 'quiz__feedback-correct' : 'quiz__feedback-incorrect'}`}
            >
              <p className={`quiz__feedback-title ${feedback.isCorrect ? 'quiz__feedback-title-correct' : 'quiz__feedback-title-incorrect'}`}>
                {feedback.isCorrect ? 'TOP !' : 'OUPS !'}
              </p>
              {feedback.feedback && (
                <p className="quiz__feedback-text">
                  {feedback.feedback}
                </p>
              )}
            </Alert>
          )}
          */}

          {/* Action Button */}
          <Box className="quiz__actions">
            {!isAnswered ? (
              <Button
                variant="contained"
                onClick={handleSubmitAnswer}
                disabled={selectedAnswer === null || submitting}
                className="button-primary quiz__button"
              >
                {submitting ? t('quiz.validating') : t('quiz.submitAnswer')}
              </Button>
            ) : (
              // For diagnostic quizzes, don't show "Suivant" button (auto-advance)
              // For normal quizzes, show "Suivant" to see feedback before continuing
              !quiz.isDiagnostic && (
                <Button
                  variant="contained"
                  onClick={handleNextQuestion}
                  className="button-primary quiz__button"
                >
                  {isLastQuestion ? t('quiz.completeQuiz') : t('quiz.nextQuestion')}
                </Button>
              )
            )}
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

export default Quiz;
